import { isAxiosError } from 'axios';
import { ChangeEventHandler, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useWindowBlankAction } from '@/hooks/useWindowBlankAction';
import { useAppointmentsStore } from '@/stores/appointments';
import {
  TInstitutionsStore,
  useInstitutionsStore,
} from '@/stores/institutions';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast';
import { IUserSlice, useUsersStore } from '@/stores/users';
import { ERouterNames, EStatusAppointment, TAppointmentCamel } from '@/types';
import {
  ESocketListenMessageType,
  ESocketSendMessageType,
} from '@/types/socket';
import { handleAxiosError } from '@/utils/handleAxiosError';
import { popupSoundPlayer } from '@/utils/soundPlayer';

const filterByOnDemand = (
  appointments: TAppointmentCamel[],
  getService: TInstitutionsStore['getService'],
  checkRoleIds: IUserSlice['checkRoleIds'],
) =>
  appointments.filter(
    ({ scheduleType, status, institutionServiceId }) =>
      scheduleType === 'on_demand' &&
      status.at(-1)?.action === EStatusAppointment.PENDING_CONFIRMATION &&
      checkRoleIds(
        getService(institutionServiceId)?.userInstitutionRoleIds ?? [],
      ),
  );

export const useOnDemandAppointments = () => {
  const [openSlideout, setOpenSlideout] = useState(false);
  const { t } = useTranslation();
  const { getService, currentInstitutionId } = useInstitutionsStore(
    (state) => ({
      getService: state.getService,
      currentInstitutionId: state.currentInstitution?.id,
    }),
  );
  const checkRoleIds = useUsersStore((state) => state.checkRoleIds);
  const setToast = useToastStore((state) => state.setToast);
  const { appointments, confirmAppointment } = useAppointmentsStore(
    (state) => ({
      appointments: state.appointments,
      confirmAppointment: state.confirmAppointment,
    }),
  );
  const [appointmentsOnDemand, setAppointmentsOnDemand] = useState(
    filterByOnDemand(appointments, getService, checkRoleIds),
  );

  const { userData, saveUserInfo } = useUsersStore((state) => ({
    userData: state.userData,
    saveUserInfo: state.saveUserInfo,
  }));
  const { sendMessage, listenMessage, stopListenMessage, isConnected } =
    useSocketStore((state) => ({
      sendMessage: state.sendMessage,
      listenMessage: state.listenMessage,
      stopListenMessage: state.stopListenMessage,
      isConnected: state.isConnected,
    }));

  const handleToggleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    void saveUserInfo({ meta: { isAvailable: event.target.checked } });
  };

  const isAvailable = !!userData?.meta?.isAvailable;
  const isAvailableDisabled =
    userData?.availability?.inWaitingRoom ||
    userData?.availability?.isInMeeting;

  useEffect(() => {
    if (!isConnected || !currentInstitutionId) return;

    sendMessage({
      type: ESocketSendMessageType.ON_DEMAND_START,
      body: { institutionId: currentInstitutionId },
    });
  }, [currentInstitutionId, isConnected, sendMessage]);
  useEffect(() => {
    listenMessage({
      type: ESocketListenMessageType.ON_DEMAND_UPDATED,
      callback: ({ appointment }) => {
        setAppointmentsOnDemand((prev) => {
          const exists = prev.some((a) => a.id === appointment.id);
          const isPendingConfirmation =
            appointment.status.at(-1)?.action ===
            EStatusAppointment.PENDING_CONFIRMATION;

          const updated = exists
            ? prev.map((a) =>
                a.id === appointment.id ? { ...a, ...appointment } : a,
              )
            : [...prev, appointment];

          if (isPendingConfirmation && isAvailable && !isAvailableDisabled) {
            void popupSoundPlayer.play();
            setOpenSlideout(true);
          }

          return filterByOnDemand(updated, getService, checkRoleIds);
        });
      },
    });
    return () => {
      stopListenMessage(ESocketListenMessageType.ON_DEMAND_UPDATED);
    };
  }, [
    checkRoleIds,
    getService,
    isAvailable,
    isAvailableDisabled,
    listenMessage,
    stopListenMessage,
  ]);

  const { runWithWindow, loading } = useWindowBlankAction();

  const onAppointmentAccept = async (appointmentId: string) => {
    const appointment = appointmentsOnDemand.find(
      ({ id }) => id === appointmentId,
    );
    if (!appointment) return;
    await runWithWindow(
      async (window) => {
        await confirmAppointment(appointment.id);
        setAppointmentsOnDemand((pr) =>
          pr.filter(({ id }) => id !== appointmentId),
        );
        if (appointment.waitingRoom?.id && !appointment.waitingRoom?.endedAt) {
          window.location.href = generatePath(`/${ERouterNames.WAITING_ROOM}`, {
            waitingRoomId: appointment?.waitingRoom?.id ?? '',
          });
          return;
        }
        const isVideo = appointment?.communicationChannel === 'video';
        const encounterUrl = generatePath(`/${ERouterNames.ENCOUNTER}`, {
          chatId: appointment?.chatId ?? '',
        });
        const chatUrl = generatePath(`/${ERouterNames.CHAT}`, {
          chatId: appointment?.chatId ?? '',
        });
        window.location.href = isVideo ? encounterUrl : chatUrl;
      },
      (err) => {
        if (!isAxiosError(err)) return;
        // TODO: handle errors.
        if (
          err.response &&
          [400, 403, 409, 500].includes(err.response.status)
        ) {
          setToast({
            title: t('info'),
            description: t(
              'general-layout.another-provider-has-accepted-the-request-to-join-a-video-encounter-no-further-action-is-required',
            ),
            variant: 'info',
          });
          return;
        }
        handleAxiosError(err, setToast);
      },
    );
  };

  const onAppointmentDecline = (appointmentId: string) =>
    setAppointmentsOnDemand((pr) =>
      pr.filter(({ id }) => id !== appointmentId),
    );

  return {
    loading,
    onAppointmentAccept,
    onAppointmentDecline,
    isAvailable,
    isAvailableDisabled,
    handleToggleChange,
    appointmentsOnDemand,
    openSlideout,
    setOpenSlideout,
  };
};
